/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { IAppState } from '../../core/redux/IAppState';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { getError, getLoading } from '../../core/redux/api/module';
import { authStateAction } from '../authAction';
import { getUserAction } from '../getUserInfoAction';
import { getResortDetailsAction } from '../../resortDetails/getResortDetailsAction';
import { selectedResortAction } from './selectedResortAction';
import { getConfigAction } from '../config/getConfigAction';

export function useAuthBootstrap() {
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading: isAuthLoading, user, getAccessTokenSilently, loginWithRedirect} = useAuth0();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const userState = useSelector((state: IAppState) => state.userState);
  const selectedResort = useSelector((state: IAppState) => state.selectedResort);
  const isLoadingUser = useSelector((state: IAppState) => !!getLoading(state, ACTION_TYPE.GET_USER_REQUEST, ACTION_TYPE.GET_USER_REQUEST));
  const isLoadingResorts = useSelector((state: IAppState) => state.resortDetails?.isLoadingResorts);
  const isLoadingConfig = useSelector((state: IAppState) => !!getLoading(state, ACTION_TYPE.APP_CONFIG_REQUEST, ACTION_TYPE.APP_CONFIG_REQUEST));
  const configError = useSelector((state: IAppState) => getError(state, ACTION_TYPE.APP_CONFIG_REQUEST, ACTION_TYPE.APP_CONFIG_REQUEST));
  const isDefaultConfig = useSelector((state: IAppState) => !state.resortToolsConfig.isConfigLoaded);

  // Avoid requesting token multiple times
  const tokenRequestedRef = useRef(false);
  const resortSelectedOnceRef = useRef(false);

  // 1) Fetch access token once Auth0 is ready
  useEffect(() => {
    const fetchAccessToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        setAccessToken(token);

        // Decode & set auth in Redux
        let decoded: any;
        try {
          decoded = jwtDecode(token);
        } catch (e) {
          console.warn('JWT decode failed:', e);
        }

        dispatch(
          authStateAction({
            username: user?.name,
            isLoggedIn: true,
            isLoggingIn: false,
            bearerToken: token,
            bearerTokenExp: decoded?.['exp'],
          })
        );

        // Load resorts + user profile
        dispatch(getResortDetailsAction());
        if (user?.sub) dispatch(getUserAction(user.sub));
      } catch (e) {
        console.error('getAccessTokenSilently failed:', e);
        
        // Force re-login to obtain a new session/refresh token
        await loginWithRedirect({
            authorizationParams: {
              prompt: 'login',               // forces re-authentication
              audience: 'resort-tools-api',  
              scope: 'openid offline_access reports:read', 
            },
            // Return to current route after re-login
            appState: { returnTo: window.location.pathname },
        });
      }
    };

    if (!tokenRequestedRef.current && isAuthenticated && !isAuthLoading) {
      tokenRequestedRef.current = true;
      fetchAccessToken();
    }
  }, [isAuthenticated, isAuthLoading, getAccessTokenSilently, dispatch, user, loginWithRedirect]);

  // 2) Once resorts are loaded and user profile available, select a default resort
  useEffect(() => {
    const hasResorts = (userState?.resortLocations?.length ?? 0) > 0;

    if (
      accessToken &&
      userState &&
      !isLoadingResorts &&
      hasResorts &&
      !selectedResort &&
      !resortSelectedOnceRef.current
    ) {
      const firstResort = userState.resortLocations![0];
      dispatch(selectedResortAction(firstResort));
      resortSelectedOnceRef.current = true;
    }
  }, [accessToken, userState, isLoadingResorts, selectedResort, dispatch]);

  // 3) When resort is selected, ensure resort config is loaded
  useEffect(() => {
    if (selectedResort && resortSelectedOnceRef.current && accessToken) {
      if (!isLoadingConfig) {
        dispatch(getConfigAction(selectedResort.resortId));
      }
    }
  }, [selectedResort, isLoadingConfig, accessToken, dispatch]);

  return {
    accessToken,
    isAuthLoading,
    isLoadingUser,
    isLoadingResorts,
    isLoadingConfig,
    selectedResort,
    userState,
    configError,
    isDefaultConfig,
  };
}
